import { FC, useMemo } from 'react';
import { Tag, Button, Text } from '@livechat/design-system-react-components';

import { useProducts } from 'hooks/use-products';
import { Logo } from 'components/logo/Logo';
import { useGetMe } from 'hooks/use-get-me';
import { getAppLinkByName, getProductByName, sortProducts } from 'helpers/product';
import { OrganizationGraphQLResponse } from 'hooks/use-organizations';
import Location from 'services/Location';
import { pushExternalRedirectToDataLayer } from 'helpers/tracking';
import { parseJwt } from 'helpers/token';
import { saveItem } from 'helpers/storage/local-storage';
import Amplitude from 'services/Amplitude';

import {
  contentContainerStyles,
  externalAppTagStyles,
  tagTextStyles,
  tagsContainerStyles,
  noProductsStyles,
} from './styles';

type Props = {
  organization: OrganizationGraphQLResponse;
};

export const ExternalAppChooserContent: FC<Props> = ({ organization }) => {
  const { roles } = useGetMe();
  const { getRedirectUrl } = useProducts();

  const state = Location.getHashParam('state');
  const requiredProduct = Location.getHashParam('product');
  const token = parseJwt(state);

  const hasAccessToProduct = (organizationProduct: string): boolean =>
    roles.some(
      (role) =>
        role.organization_id === organization.organization_id &&
        role.product === organizationProduct
    );

  const sortedProducts = useMemo(
    () => sortProducts(organization.products.map((product) => product.name)),
    [organization.products]
  );

  const handleContinueClick = () => {
    saveItem('signup', '1');
    pushExternalRedirectToDataLayer(getAppLinkByName(requiredProduct));
    Amplitude.logEvent(null, 'EXTERNAL_APP_CHOOSER_CONTINUE_CLICKED');
  };

  return (
    <div css={contentContainerStyles}>
      <div css={tagsContainerStyles}>
        {organization.products.length > 0 ? (
          sortedProducts.map((product) => (
            <div
              key={`${product}_${organization.organization_id}`}
              data-testid={`external-app-product-button-${product.toLowerCase()}`}
            >
              {hasAccessToProduct(product) && (
                <Tag outline css={externalAppTagStyles}>
                  <Logo product={getProductByName(product)} size="small" />
                  <Text css={tagTextStyles}>{product}</Text>
                </Tag>
              )}
            </div>
          ))
        ) : (
          <Text bold css={noProductsStyles}>
            You don&apos;t have products in this organization
          </Text>
        )}
      </div>
      <Button
        size="compact"
        href={getRedirectUrl(requiredProduct, organization.organization_id, { jwtParams: token })}
        onClick={handleContinueClick}
      >
        Continue
      </Button>
    </div>
  );
};
